import { useCallback, useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { ChevronDown, Map as MapIcon, Plus, RefreshCcw, Swords } from 'lucide-react'
import Header from '@/components/layout/Header'
import { Button } from '@/components/ui/button'
import RosterConfig from '@/components/matches/RosterConfig'
import MatchFormDialog from '@/components/matches/MatchFormDialog'
import MatchDetail from '@/components/matches/MatchDetail'
import FactionIcon from '@/components/matches/FactionIcon'
import EventDetailPanel from '@/components/events/EventDetailPanel'
import type { SignUp } from '@/components/events/types'
import { useAuth, isManager, isOperator, isStrategyTeam } from '@/contexts/AuthContext'
import { useLanguage } from '@/contexts/LanguageContext'
import { clock, dayParts, relativeTo, useNow } from '@/lib/matchTime'
import { stepStates, type Match, type StepState } from '@/components/matches/types'

/** One segment of a list row's step track. */
function Tick({ state }: { state: StepState }) {
  const tone = state === 'action' ? 'bg-primary' : state === 'done' ? 'bg-foreground/55' : 'bg-white/10'
  return <span className={`h-1 flex-1 rounded-full ${tone}`} />
}

/**
 * A match in the list. The three ticks are the match's steps — sign-up,
 * position, strategy — as they stand for the viewer; the line under them says
 * the one thing the viewer should know, in crimson only when it asks for action.
 */
function MatchRow({ match, selected, onSelect, now }: {
  match: Match
  selected: boolean
  onSelect: () => void
  now: number
}) {
  const { user } = useAuth()
  const { T } = useLanguage()
  const ev = match.event
  const day = ev.start_time ? dayParts(ev.start_time, T) : null
  const upcoming = !ev.start_time || ev.start_time > now
  const mine = user ? ev.sign_ups.find(s => s.user_id === user.id) : undefined

  let status: { text: string; action?: boolean } | null = null
  let states: Record<'signup' | 'position' | 'strategy', StepState> | null = null
  if (match.local) {
    states = stepStates(match, user?.id, now)
    const pos = match.progress?.position
    if (states.position === 'action') status = { text: T.matches.listConfirm, action: true }
    else if (states.signup === 'action') status = { text: T.matches.listSignUp, action: true }
    else if (pos?.role === 'substitute') status = { text: T.matches.listBench }
    else if (pos?.status === 'yes') status = { text: T.matches.listInRoster }
    else if (pos?.status === 'no') status = { text: T.matches.listDeclined }
    else if (mine) status = { text: T.matches.listSigned.replace('{class}', mine.class_name) }
  } else if (mine) {
    status = { text: T.matches.listSigned.replace('{class}', mine.class_name) }
  }

  return (
    <button
      onClick={onSelect}
      aria-current={selected}
      className={`relative flex w-full gap-3 border-b border-white/10 px-3 py-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/60 ${
        selected ? 'bg-white/[0.06]' : 'hover:bg-white/[0.03]'
      } ${upcoming ? '' : 'opacity-60'}`}
    >
      {selected && <span className="absolute inset-y-0 left-0 w-0.5 bg-primary" />}

      {/* Date block: the day is what people scan the list by. */}
      <div className="w-11 shrink-0 text-center">
        {day ? (
          <>
            <div className="text-[10px] text-muted-foreground">{day.weekday}</div>
            <div className="font-display text-2xl font-bold leading-none tabular-nums">{day.day}</div>
            <div className="text-[10px] text-muted-foreground">{day.month}</div>
          </>
        ) : <div className="font-display text-lg text-muted-foreground/40">-</div>}
      </div>

      <div className="min-w-0 flex-1 space-y-1.5">
        <div className="flex items-start gap-2">
          <p className="min-w-0 flex-1 truncate text-sm font-semibold leading-tight">{ev.title || '-'}</p>
          {ev.staff_only && (
            <span className="shrink-0 rounded-sm border border-amber-400/30 px-1 text-[9px] leading-4 text-amber-400/80">
              {T.matches.staffOnly}
            </span>
          )}
          {match.manual && (
            <span className="shrink-0 rounded-sm border border-white/10 px-1 text-[9px] leading-4 text-muted-foreground/60">
              {T.matches.external}
            </span>
          )}
        </div>

        {ev.teams && ev.teams.some(t => t.faction) && (
          <div className="flex items-center gap-1">
            {ev.teams.map((t, i) => <FactionIcon key={i} faction={t.faction} className="h-3.5 w-3.5" />)}
          </div>
        )}

        <p className="flex flex-wrap items-center gap-x-2 text-[11px] text-muted-foreground">
          {ev.start_time && <span className="tabular-nums">{clock(ev.start_time)}</span>}
          {ev.start_time && <span>{relativeTo(ev.start_time, now, T)}</span>}
          {ev.map_name && (
            <span className="flex items-center gap-1 text-muted-foreground/70">
              <MapIcon className="h-2.5 w-2.5" />{ev.map_name}
            </span>
          )}
        </p>

        {states && (
          <div className="flex gap-1" title={`${T.matches.stepSignup} · ${T.matches.stepPosition} · ${T.matches.stepStrategy}`}>
            <Tick state={states.signup} />
            <Tick state={states.position} />
            <Tick state={states.strategy} />
          </div>
        )}

        <div className="flex items-center justify-between gap-2 text-[11px]">
          <span className="tabular-nums text-muted-foreground">
            {T.matches.playingCount.replace('{n}', String(ev.active_count))}
          </span>
          {status && (
            <span className={`truncate ${status.action ? 'font-semibold text-primary' : 'text-muted-foreground'}`}>
              {status.text}
            </span>
          )}
        </div>
      </div>
    </button>
  )
}

export default function Matches() {
  const { user } = useAuth()
  const { T } = useLanguage()
  const now = useNow()
  const canEdit = isOperator(user)
  const canManage = isManager(user)
  // The strategy team may build the roster but not send it to Discord; that
  // send stays behind canEdit (operator). See AuthContext isStrategyTeam.
  const canBuildRoster = isStrategyTeam(user)
  const [matches, setMatches] = useState<Match[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchParams, setSearchParams] = useSearchParams()
  const [selected, setSelected] = useState<string | null>(searchParams.get('select'))
  const [viewMode, setViewMode] = useState<'detail' | 'roster'>('detail')

  // The address names the open match, both ways: a DM link opens it, and
  // opening one makes the address shareable and survives a refresh. Clearing
  // the param on read (what this did before) threw the link away.
  useEffect(() => {
    if (searchParams.get('select') === (selected ?? null)) return
    const next = new URLSearchParams(searchParams)
    if (selected) next.set('select', selected)
    else next.delete('select')
    setSearchParams(next, { replace: true })
  }, [selected]) // eslint-disable-line react-hooks/exhaustive-deps

  // Create / edit dialog — `editing` null means "new match"
  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState<Match | null>(null)
  const [historyOpen, setHistoryOpen] = useState(false)
  const staleMatchRef = useRef<Match | null>(null)

  const fetchMatches = useCallback(async (force = false) => {
    setLoading(true)
    setError(null)
    const url = force ? '/api/matches?refresh=true' : '/api/matches'
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const res = await fetch(url, { credentials: 'include' })
        if (res.ok) {
          const data: Match[] = await res.json()
          // Keep previous data if server returns empty transiently
          setMatches(prev => data.length > 0 || prev.length === 0 ? data : prev)
          setLoading(false)
          return
        }
        const d = await res.json().catch(() => ({}))
        setError(d.detail ?? `Error ${res.status}`)
        setLoading(false)
        return
      } catch {
        if (attempt === 2) setError(T.matches.serverError)
        else await new Promise(r => setTimeout(r, 800 * (attempt + 1)))
      }
    }
    setLoading(false)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchMatches() }, [fetchMatches])

  const handleSelectMatch = (id: string) => {
    setSelected(id)
    setViewMode('detail')
  }

  const _foundMatch = selected ? (matches.find(m => m.event_id === selected) ?? null) : null
  if (_foundMatch) staleMatchRef.current = _foundMatch
  const selectedMatch = _foundMatch ?? (selected ? staleMatchRef.current : null)

  const handleRemoveMatch = async (match: Match) => {
    // Optimistically remove from list
    setMatches(prev => prev.filter(m => m.event_id !== match.event_id))
    if (selected === match.event_id) setSelected(null)
    const url = match.local
      ? `/api/matches/${match.event_id}`
      : `/api/matches/manual_event/${match.event_id}`
    await fetch(url, { method: 'DELETE', credentials: 'include' })
    await fetchMatches(true)
  }

  const handleSaved = async (eventId: string) => {
    await fetchMatches(true)
    if (eventId) handleSelectMatch(eventId)
  }

  /** The viewer's own sign-up changed on the detail page: mirror it in the list. */
  const handleSignUpChange = (matchId: string, su: SignUp | null) => {
    if (!user) return
    setMatches(prev => prev.map(m => {
      if (m.event_id !== matchId) return m
      const without = m.event.sign_ups.filter(s => s.user_id !== user.id)
      const signUps = su ? [...without, su] : without
      return { ...m, event: { ...m.event, sign_ups: signUps, active_count: signUps.filter(s => s.is_active).length } }
    }))
  }

  // Soonest first among what is coming (a match stays "coming" for three hours
  // after kick-off, while it is being played); the past newest first, folded.
  const live = (m: Match) => !m.event.start_time || m.event.start_time >= now - 3 * 3600
  const upcoming = matches.filter(live)
    .sort((a, b) => (a.event.start_time ?? Infinity) - (b.event.start_time ?? Infinity))
  const past = matches.filter(m => !live(m))
    .sort((a, b) => (b.event.start_time ?? 0) - (a.event.start_time ?? 0))

  // A link to a played match opens its detail; unfold the history too, so the
  // list shows the row the page is standing on instead of looking empty.
  const selectedIsPast = !!selected && past.some(m => m.event_id === selected)
  useEffect(() => {
    if (selectedIsPast) setHistoryOpen(true)
  }, [selectedIsPast])

  return (
    <>
      <Header title={T.matches.title} />
      <div className="flex h-[calc(100vh-4.25rem)] overflow-hidden">

        {/* ── List ── */}
        <aside className={`${selected ? 'hidden md:flex' : 'flex'} w-full flex-col overflow-hidden border-r border-white/10 bg-black/25 backdrop-blur-sm md:w-80 md:shrink-0`}>
          <div className="flex items-center gap-2 border-b border-white/10 px-3 py-2.5">
            <h2 className="flex flex-1 items-center gap-2.5 font-display text-[11px] font-bold uppercase tracking-[0.18em] text-foreground/70">
              <span className="h-px w-6 shrink-0 bg-primary" />
              {T.matches.upcomingTitle}
              {!loading && <span className="tabular-nums text-muted-foreground">{upcoming.length}</span>}
            </h2>
            <button
              onClick={() => fetchMatches(true)}
              disabled={loading}
              aria-label={T.matches.refresh}
              className="rounded p-1.5 text-muted-foreground/60 transition-colors hover:bg-white/5 hover:text-foreground disabled:opacity-40"
            >
              <RefreshCcw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
            </button>
            {/* Create — admin team only, moderators excluded */}
            {canManage && (
              <Button size="sm" className="h-7 gap-1 px-2.5 text-xs" onClick={() => { setEditing(null); setFormOpen(true) }}>
                <Plus className="h-3.5 w-3.5" />{T.matches.newMatch}
              </Button>
            )}
          </div>

          <div className="flex-1 overflow-y-auto">
            {error && <p className="px-4 py-3 text-xs text-destructive">{error}</p>}
            {loading && matches.length === 0 && !error && Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="flex gap-3 border-b border-white/10 px-3 py-3 animate-pulse">
                <div className="h-12 w-11 rounded-sm bg-white/5" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 w-3/4 rounded bg-white/8" />
                  <div className="h-3 w-1/2 rounded bg-white/6" />
                  <div className="h-1 w-full rounded bg-white/6" />
                </div>
              </div>
            ))}

            {!loading && upcoming.length === 0 && !error && (
              <div className="px-4 py-8 text-center">
                <p className="text-sm text-foreground/80">{T.matches.noUpcoming}</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  {canManage ? T.matches.noUpcomingStaff : T.matches.noUpcomingMember}
                </p>
              </div>
            )}

            {upcoming.map(m => (
              <MatchRow key={m.event_id} match={m} now={now}
                selected={selected === m.event_id} onSelect={() => handleSelectMatch(m.event_id)} />
            ))}

            {past.length > 0 && (
              <>
                <button
                  onClick={() => setHistoryOpen(o => !o)}
                  aria-expanded={historyOpen}
                  className="flex w-full items-center justify-between border-b border-white/10 px-4 py-2.5 text-left transition-colors hover:bg-white/[0.03]"
                >
                  <span className="font-display text-[11px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
                    {T.matches.pastTitle} <span className="tabular-nums">{past.length}</span>
                  </span>
                  <ChevronDown className={`h-3.5 w-3.5 text-muted-foreground/60 transition-transform ${historyOpen ? 'rotate-180' : ''}`} />
                </button>
                {historyOpen && past.map(m => (
                  <MatchRow key={m.event_id} match={m} now={now}
                    selected={selected === m.event_id} onSelect={() => handleSelectMatch(m.event_id)} />
                ))}
              </>
            )}
          </div>
        </aside>

        {/* ── Detail ── */}
        <div className={`${selected ? 'flex' : 'hidden md:flex'} flex-1 flex-col overflow-hidden`}>
          {!selectedMatch ? (
            <div className="flex h-full items-center justify-center p-6">
              <div className="max-w-xs space-y-2 text-center">
                <Swords className="mx-auto h-10 w-10 text-muted-foreground/20" />
                {/* A link named a match we do not have: say so. Falling back to
                    "pick a match" reads as if the link never carried one. */}
                <p className="text-sm text-foreground/80">
                  {selected && !loading ? T.matches.matchGone : T.matches.noMatchSelected}
                </p>
                <p className="text-xs text-muted-foreground">
                  {selected && !loading ? T.matches.matchGoneHint : T.matches.noMatchSelectedHint}
                </p>
                {/* On a phone the list is hidden while a match is selected, so
                    a dead link would otherwise leave no way back to it. */}
                {selected && !loading && (
                  <Button size="sm" variant="ghost" className="h-7 gap-1.5 text-xs md:hidden" onClick={() => setSelected(null)}>
                    {T.matches.backToList}
                  </Button>
                )}
              </div>
            </div>
          ) : viewMode === 'roster' ? (
            <div className="flex h-full flex-col overflow-hidden">
              <div className="shrink-0 border-b border-white/10 px-4 py-2">
                <Button size="sm" variant="ghost" className="h-7 gap-1.5 text-xs" onClick={() => setViewMode('detail')}>
                  {T.matches.backToDetails}
                </Button>
              </div>
              <div className="flex-1 overflow-hidden">
                <RosterConfig
                  key={selectedMatch.event_id}
                  threadId={selectedMatch.event_id}
                  eventId={selectedMatch.event.id}
                  signUps={selectedMatch.event.sign_ups}
                  canSend={canEdit}
                />
              </div>
            </div>
          ) : (
            <>
              <div className="shrink-0 border-b border-white/10 px-4 py-2 md:hidden">
                <Button size="sm" variant="ghost" className="h-7 gap-1.5 text-xs" onClick={() => setSelected(null)}>
                  {T.matches.backToList}
                </Button>
              </div>
              {selectedMatch.local ? (
                <MatchDetail
                  key={selectedMatch.event_id}
                  match={selectedMatch}
                  canEdit={canEdit}
                  canBuildRoster={canBuildRoster}
                  onEdit={canManage ? () => { setEditing(selectedMatch); setFormOpen(true) } : undefined}
                  onRemove={canManage ? () => handleRemoveMatch(selectedMatch) : undefined}
                  onConfigureRoster={canBuildRoster ? () => setViewMode('roster') : undefined}
                  onRefresh={() => fetchMatches(true)}
                  onSignUpChange={su => handleSignUpChange(selectedMatch.event_id, su)}
                />
              ) : (
                // The Raid Helper archive keeps the panel it was built with.
                <EventDetailPanel
                  key={selectedMatch.event_id}
                  title={selectedMatch.event.title}
                  startTime={selectedMatch.event.start_time}
                  description={selectedMatch.event.description}
                  eventId={selectedMatch.event_id}
                  signupBase="matches"
                  isExternal={selectedMatch.event.is_external}
                  discordUrl={selectedMatch.event.discord_url}
                  signUps={selectedMatch.event.sign_ups as SignUp[]}
                  onConfigureSquads={canEdit ? () => setViewMode('roster') : undefined}
                  configLabel={T.matches.configureRoster}
                  onRefresh={() => fetchMatches(true)}
                  mapName={selectedMatch.event.map_name}
                  onRemoveMatch={canEdit && selectedMatch.manual ? () => handleRemoveMatch(selectedMatch) : undefined}
                  onSignUpChange={su => handleSignUpChange(selectedMatch.event_id, su)}
                />
              )}
            </>
          )}
        </div>
      </div>

      <MatchFormDialog
        open={formOpen}
        match={editing ? {
          event_id: editing.event_id,
          title: editing.event.title,
          start_time: editing.event.start_time,
          briefing_time: editing.event.briefing_time,
          map_name: editing.event.map_name,
          teams: editing.event.teams,
          description: editing.event.description,
        } : null}
        onClose={() => setFormOpen(false)}
        onSaved={handleSaved}
      />
    </>
  )
}
